
import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function Blog() {
  const [activePost, setActivePost] = useState(null);

  const featuredPosts = [
    {
      id: 1,
      category: "SALON SOFTWARE",
      title: "AI Salon Software That Increases Bookings",
      desc: "How intelligent booking systems help salons automate appointments and grow revenue.",
      img: "/img/strategy-final.jpg"
    },
    {
      id: 2,
      category: "BOOKING SYSTEM",
      title: "24/7 Appointment Scheduling With AI Receptionist",
      desc: "Modern salons are replacing missed calls and manual scheduling with automation.",
      img: "/img/stilai-final.jpg"
    },
    {
      id: 3,
      category: "SALON GROWTH",
      title: "Scale Your Salon Business With Automation",
      desc: "Reduce admin work, improve customer experience, and run smarter operations.",
      img: "/img/lifestyle-final.jpg"
    }
  ];

  return (
    <div className="blog-page-wrapper">
      <Helmet>
        <title>
          AI Salon Software Blog | Booking System, Salon Automation & Growth |
          Maniera
        </title>

        <meta
          name="description"
          content="Discover AI salon software insights, booking systems, salon automation, appointment scheduling tools, and growth strategies for beauty businesses."
        />

        <meta
          name="keywords"
          content="salon software, AI salon software, booking system for salons, appointment scheduling software, salon management software, salon automation, AI receptionist"
        />

        <meta name="robots" content="index,follow,max-image-preview:large" />

        <link rel="canonical" href="https://www.manieradigital.com/blog" />

        <meta property="og:title" content="AI Salon Software Blog | Maniera" />
        <meta
          property="og:description"
          content="Booking systems, salon automation, AI receptionist tools, and growth insights."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/img/maniera-hero-v1.jpg" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Maniera Blog",
            url: "https://www.manieradigital.com/blog",
            description:
              "Insights about AI salon software, booking systems, and automation."
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section
        style={{
          backgroundImage: 'url("/img/maniera-hero-v1.jpg")',
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "65vh",
          display: "flex",
          alignItems: "center",
          position: "relative"
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,.55), rgba(0,0,0,.92))"
          }}
        />

        <Container style={{ position: "relative", zIndex: 2 }}>
          <Row className="justify-content-center text-center">
            <Col lg={9}>
              <h1
                style={{
                  color: "#bca24c",
                  fontFamily: "Prata, serif",
                  fontSize: "clamp(3rem,8vw,5.5rem)"
                }}
              >
                AI Salon Software Blog & Insights
              </h1>

              <p
                style={{
                  color: "#fff",
                  letterSpacing: "4px",
                  textTransform: "uppercase",
                  opacity: 0.9
                }}
              >
                Booking Systems • Salon Automation • AI Receptionist
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* INTRO */}
      <section className="py-5" style={{ background: "#fff" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <h2
                style={{
                  fontFamily: "Prata, serif",
                  fontSize: "2.4rem"
                }}
              >
                Modern Salon Software For Smart Beauty Businesses
              </h2>

              <p style={{ color: "#555", lineHeight: "1.9" }}>
                The beauty industry is evolving fast. Clients expect instant
                booking, quick responses, smooth communication, and premium
                service experiences. Traditional scheduling systems based on
                calls, notebooks, or slow messaging no longer meet modern
                expectations.
              </p>

              <p style={{ color: "#555", lineHeight: "1.9" }}>
                AI salon software helps automate appointments, reduce admin
                workload, improve retention, and create scalable growth systems.
                Maniera explores the future of booking automation, client
                management, and salon business performance.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* POSTS */}
      <section className="py-5" style={{ background: "#f8f8f8" }}>
        <Container>
          <h2
            className="mb-5"
            style={{
              fontFamily: "Prata, serif",
              fontSize: "2.5rem"
            }}
          >
            Latest Articles
          </h2>

          <Row className="gy-5">
            {featuredPosts.map((post) => (
              <Col xs={12} md={4} key={post.id}>
                <article>
                  <div
                    style={{
                      height: "400px",
                      overflow: "hidden",
                      position: "relative"
                    }}
                    className="mb-3"
                  >
                    <img
                      src={post.img}
                      alt={post.title}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />

                    <div
                      style={{
                        position: "absolute",
                        top: 20,
                        left: 20,
                        background: "#bca24c",
                        color: "#fff",
                        padding: "5px 14px",
                        fontSize: ".7rem",
                        fontWeight: "bold",
                        letterSpacing: "1px"
                      }}
                    >
                      {post.category}
                    </div>
                  </div>

                  <h3
                    style={{
                      fontFamily: "Prata, serif",
                      fontSize: "1.45rem"
                    }}
                  >
                    {post.title}
                  </h3>

                  <p style={{ color: "#666" }}>{post.desc}</p>

                  <p
                    onClick={() =>
                      setActivePost(activePost === post.id ? null : post.id)
                    }
                    style={{
                      color: "#bca24c",
                      cursor: "pointer",
                      fontWeight: 600
                    }}
                  >
                    {activePost === post.id
                      ? "Hide article ↑"
                      : "Read full article →"}
                  </p>

                  {activePost === post.id && (
                    <div style={{ color: "#444", lineHeight: "1.9" }}>
                      {post.id === 1 && (
                        <>
                          <p>
                            AI salon software helps beauty businesses increase
                            bookings by removing friction from the customer
                            journey.
                          </p>
                          <p>
                            Clients can instantly reserve appointments without
                            waiting for manual confirmation.
                          </p>
                          <p>
                            Faster booking experiences often lead to higher
                            conversion and better retention.
                          </p>
                        </>
                      )}

                      {post.id === 2 && (
                        <>
                          <p>
                            AI receptionists can answer booking requests,
                            schedule visits, send reminders, and manage changes
                            24/7.
                          </p>
                          <p>
                            This reduces missed calls and ensures salons never
                            lose leads after business hours.
                          </p>
                          <p>
                            Automation improves customer satisfaction and saves
                            staff time.
                          </p>
                        </>
                      )}

                      {post.id === 3 && (
                        <>
                          <p>
                            Salon growth depends on systems that scale with
                            demand.
                          </p>
                          <p>
                            Automated scheduling, CRM, and communication tools
                            help owners expand without operational chaos.
                          </p>
                          <p>
                            Smart infrastructure supports long-term business
                            growth.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </article>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* LONG SEO BLOCK */}
      <section className="py-5" style={{ background: "#fff" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <h2
                style={{
                  fontFamily: "Prata, serif",
                  fontSize: "2.3rem"
                }}
              >
                Why AI Booking Systems Matter In 2026
              </h2>

              <p style={{ color: "#555", lineHeight: "1.95" }}>
                Customers now expect convenience. They want to book appointments
                instantly, receive reminders automatically, and reschedule with
                zero friction. Salons using outdated systems risk losing clients
                to faster competitors.
              </p>

              <p style={{ color: "#555", lineHeight: "1.95" }}>
                AI booking systems solve this by combining automation,
                scheduling, communication, and client management in one place.
                Owners save time while customers receive a premium experience.
              </p>

              <p style={{ color: "#555", lineHeight: "1.95" }}>
                Businesses that adopt modern salon software often improve staff
                productivity, reduce cancellations, and create stronger customer
                loyalty through smoother operations.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PREMIUM END SECTION */}
      <section
        className="py-5 text-center"
        style={{
          background: "linear-gradient(135deg, #0f0f0f, #1a1a1a)",
          color: "#fff",
          borderTop: "1px solid rgba(188,162,76,0.25)"
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={9} lg={8}>
              <p
                style={{
                  color: "#bca24c",
                  letterSpacing: "4px",
                  textTransform: "uppercase",
                  fontSize: ".8rem",
                  marginBottom: "15px",
                  opacity: 0.9
                }}
              >
                Maniera Intelligence
              </p>

              <h2
                style={{
                  fontFamily: "Prata, serif",
                  fontSize: "clamp(2rem,5vw,3.4rem)",
                  lineHeight: "1.3",
                  marginBottom: "20px"
                }}
              >
                The Future of Salon Growth Is Automated
              </h2>

              <p
                style={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: "1.05rem",
                  lineHeight: "1.9",
                  maxWidth: "760px",
                  margin: "0 auto 30px auto"
                }}
              >
                Maniera combines AI booking systems, automated client
                communication, and premium salon software infrastructure to help
                beauty businesses grow faster, reduce admin workload, and
                deliver elite customer experiences.
              </p>

              <div
                style={{
                  width: "90px",
                  height: "2px",
                  background: "#bca24c",
                  margin: "0 auto 28px auto"
                }}
              />

              <div className="d-flex justify-content-center gap-4 flex-wrap">
                <Link
                  to="/services"
                  style={{
                    color: "#111",
                    background: "#bca24c",
                    padding: "12px 28px",
                    textDecoration: "none",
                    fontWeight: "600",
                    borderRadius: "2px"
                  }}
                >
                  Explore Services
                </Link>

                <Link
                  to="/contact"
                  style={{
                    color: "#fff",
                    border: "1px solid #bca24c",
                    padding: "12px 28px",
                    textDecoration: "none",
                    fontWeight: "600",
                    borderRadius: "2px"
                  }}
                >
                  Start Project
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}

